import axiosInstance from './axiosInstance';

// Create a new WordPress connection
export const createWordpressConnection = (data) =>
	axiosInstance.post('/wordpress/connections', data);

// Get all WordPress connections for the authenticated user
export const getAllWordpressConnections = () =>
	axiosInstance.get('/wordpress/connections');

// Get a single WordPress connection by ID
export const getWordpressConnectionById = (id) =>
	axiosInstance.get(`/wordpress/connections/${id}`);

// Update a WordPress connection by ID
export const updateWordpressConnection = (id, data) =>
	axiosInstance.put(`/wordpress/connections/${id}`, data);

// Delete a WordPress connection by ID
export const deleteWordpressConnection = (id) =>
	axiosInstance.delete(`/wordpress/connections/${id}`);

// Discover available endpoints for a WordPress connection
export const discoverWordpressEndpoints = (id) =>
	axiosInstance.post(`/wordpress/connections/${id}/discover-endpoints`);

// Test connection and discover endpoints with connection data
export const testWordpressConnection = (connectionData) =>
	axiosInstance.post('/wordpress/test-connection', connectionData);

// Update selected endpoints for a WordPress connection
export const updateWordpressSelectedEndpoints = (id, selectedEndpoints) =>
	axiosInstance.put(`/wordpress/connections/${id}/selected-endpoints`, {
		selectedEndpoints,
	});

// Get posts for a specific WordPress connection by ID
export const getWordpressConnectionPosts = (id) =>
	axiosInstance.get(`/wordpress/connections/${id}/posts`);

// Get available post types for a WordPress connection
export const getWordpressConnectionPostTypes = (id) =>
	axiosInstance.get(`/wordpress/connections/${id}/types`);

// Get custom posts of a specific type for a WordPress connection by ID
export const getWordpressConnectionCustomPosts = (id, type) =>
	axiosInstance.get(`/wordpress/connections/${id}/custom/${type}`);

// Get posts using a custom endpoint URL for a WordPress connection
export const getWordpressConnectionCustomEndpointPosts = (id, endpointUrl) =>
	axiosInstance.post(`/wordpress/connections/${id}/custom-endpoint`, {
		endpointUrl,
	});

// Update a post on a WordPress site
export const updateWordpressPost = (
	connectionId,
	postType,
	postId,
	updateData,
) =>
	axiosInstance.put(
		`/wordpress/connections/${connectionId}/${postType}/post/${postId}`,
		updateData,
	);

// Create a new post on a WordPress site
export const createWordpressPost = (connectionId, postType, createData) =>
	axiosInstance.post(
		`/wordpress/connections/${connectionId}/${postType}/post`,
		createData,
	);

// Example API function
export const getWordpressConnectionPost = (connectionId, postType, postId) =>
	axiosInstance.get(
		`/wordpress/connections/${connectionId}/${postType}/post/${postId}`,
	);

// Delete a post on a WordPress site
export const deleteWordpressPost = (connectionId, postType, postId) =>
	axiosInstance.delete(
		`/wordpress/connections/${connectionId}/${postType}/post/${postId}`,
	);

// Import enabled post types to local DB, then run AI analysis once
export const saveAllPostTypesToLocalAndAnalyze = (connectionId, postTypes = []) =>
	axiosInstance.post(
		`/wordpress/connections/${connectionId}/save-all-and-analyze`,
		{ postTypes },
	);

// Get all locally saved posts for a connection (optionally by postType)
export const getLocalContent = (connectionId, postType) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/local-content`, {
		params: postType ? { postType } : {},
	});

// Get AI summary for a connection
export const getWordpressConnectionAISummary = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/ai-summary`);

// Trigger AI analysis for a connection (analyze only)
export const triggerWordpressConnectionAnalysis = (connectionId) =>
	axiosInstance.post(`/wordpress/connections/${connectionId}/analyze`);

export const syncConnectionContent = (connectionId, postType) =>
	axiosInstance.post(`/wordpress/connections/${connectionId}/sync`, {
		...(postType ? { postType } : {}),
	});

export const analyzeConnectionContent = (connectionId) =>
	axiosInstance.post(`/wordpress/connections/${connectionId}/analyze`);

export const getConnectionHealth = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/health`);

export const getAiReadiness = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/ai-readiness`);

export const getDiscoveryCache = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/discovery-cache`);

export const getMonthlyAiGenerationUsage = (companyId) =>
	axiosInstance.get('/wordpress/ai-generation/monthly-usage', {
		params: companyId ? { companyId } : {},
	});

// Add the new auto-generate posts function
export const autoGeneratePostsWithAI = (
	connectionId,
	data,
	topicImages = {},
) => {
	// Create FormData if we have files to upload
	if (Object.keys(topicImages).length > 0) {
		const formData = new FormData();

		// Add the JSON data
		formData.append(
			'data',
			JSON.stringify({
				...data,
				connectionId,
			}),
		);

		// Add each topic image to the form data
		Object.entries(topicImages).forEach(([topicId, imageFile]) => {
			formData.append(`topic_images_${topicId}`, imageFile);
		});

		// Send multipart form data
		// Note: Don't set Content-Type header - let axios and browser handle it automatically
		return axiosInstance.post(
			`/wordpress/connections/${connectionId}/ai-auto-post-generator`,
			formData,
		);
	} else {
		// No files, just send regular JSON
		return axiosInstance.post(
			`/wordpress/connections/${connectionId}/ai-auto-post-generator`,
			{
				...data,
				connectionId,
			},
		);
	}
};

// Get scheduled posts for a connection
export const getScheduledPosts = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/scheduled-posts`);

// Get a single scheduled post by ID
export const getScheduledPostById = (postId) =>
	axiosInstance.get(`/wordpress/scheduled-posts/${postId}`);

// Update scheduled post status
export const updateScheduledPostStatus = (postId, status) =>
	axiosInstance.patch(`/wordpress/scheduled-posts/${postId}/status`, {
		status,
	});

// Delete a scheduled post
export const deleteScheduledPost = (postId, { scope = 'single' } = {}) =>
	axiosInstance.delete(`/wordpress/scheduled-posts/${postId}`, {
		params: { scope },
	});

export const getScheduledPostSeries = (postId) =>
	axiosInstance.get(`/wordpress/scheduled-posts/${postId}/series`);

export const updateScheduledPostSeries = (postId, data) =>
	axiosInstance.put(`/wordpress/scheduled-posts/${postId}/series`, data);

// Get scheduled posts statistics for a connection
export const getScheduledPostsStats = (connectionId) =>
	axiosInstance.get(
		`/wordpress/connections/${connectionId}/scheduled-posts/stats`,
	);

// AI Content Generation APIs
export const generateAITitle = (data) =>
	axiosInstance.post('/wordpress/ai/generate-title', data);

export const generateAIContent = (data) =>
	axiosInstance.post('/wordpress/ai/generate-content', data);

export const generateAIExcerpt = (data) =>
	axiosInstance.post('/wordpress/ai/generate-excerpt', data);

export const generateAIMetaDescription = (data) =>
	axiosInstance.post('/wordpress/ai/generate-meta-description', data);

export const generateAITags = (data) =>
	axiosInstance.post('/wordpress/ai/generate-tags', data);

export const getBlogsmithRevisionCount = () =>
	axiosInstance.get('/wordpress/ai-posts/revisions/count');

export const getBlogsmithRevisionPosts = () =>
	axiosInstance.get('/wordpress/ai-posts/revisions');

export const getConnectionBlogsmithRevisionPosts = (connectionId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/ai-posts/revisions`);

export const getBlogsmithWorkflowPost = (connectionId, postId) =>
	axiosInstance.get(`/wordpress/connections/${connectionId}/ai-posts/${postId}`);

export const updateBlogsmithWorkflowPost = (connectionId, postId, data) =>
	axiosInstance.patch(`/wordpress/connections/${connectionId}/ai-posts/${postId}`, data);

export const uploadBlogsmithWorkflowPostMedia = (connectionId, postId, file) => {
	const formData = new FormData();
	formData.append('file', file);
	return axiosInstance.post(
		`/wordpress/connections/${connectionId}/ai-posts/${postId}/media`,
		formData,
	);
};

export const requestBlogsmithClientReview = (connectionId, postId, { note } = {}) =>
	axiosInstance.post(
		`/wordpress/connections/${connectionId}/ai-posts/${postId}/request-review`,
		{ note },
	);

export const publishBlogsmithWorkflowPost = (connectionId, postId, { note } = {}) =>
	axiosInstance.post(
		`/wordpress/connections/${connectionId}/ai-posts/${postId}/publish`,
		{ note },
	);

export const getBlogsmithWorkflowAuditLog = (connectionId, postId) =>
	axiosInstance.get(
		`/wordpress/connections/${connectionId}/ai-posts/${postId}/audit-log`,
	);
